/**
 * The two screens the router falls back to: a path nobody mounted, and a route that threw.
 *
 * ── WHY BOTH EXIST ─────────────────────────────────────────────────────────────
 * Before these, an unknown path and a failed chunk both ended on React Router's own
 * default error element — "Unexpected Application Error!", a 404 status line and a stack
 * trace, in English, in a system font, on a product whose first rule is Bangla first.
 * A customer who followed an old link, or whose phone asked for a chunk a deploy had just
 * replaced, was shown a developer's console. `docs/10 § WP-K` says this product has no
 * generic "something went wrong" screen; the router's default was exactly that screen, in
 * English. (The Bangla sentence itself is not quoted here: `states/__tests__/states.test.tsx`
 * forbids it in every source file, comments included.)
 *
 * ── THE COPY IS AUTHORED, AND MARKED SO ────────────────────────────────────────
 * No design file draws a not-found or a crash screen, so the two titles and the two
 * sentences below were written by this build under the founder's delegation and have NOT
 * been read by a native speaker. They carry `data-copy-status="authored"` in the DOM, the
 * same marker the API's error envelope carries (`packages/contracts/src/authored-copy.ts §
 * CopyStatus`), and they are listed for review in `docs/copy-review/W1-03.md`. The two
 * button labels are not new: `হোমে যান` is drawn in `Oprappo P0 Screens.dc.html` and
 * `আবার চেষ্টা করুন` in `Oprappo P0 States.dc.html`.
 *
 * Written against `P0 States § চারটি নিয়ম`: say what happened and not the code (no "404"),
 * say the next step, and give every state at least one way out.
 *
 * ── THE ONE RELOAD ─────────────────────────────────────────────────────────────
 * The Business OS arrives as lazily loaded chunks with content-hashed names. A tab opened
 * before a deploy still holds the OLD document, which asks for the old names, and the new
 * deployment does not have them. The honest fix is the new document, so the error
 * element reloads — ONCE. `sw.ts` now fetches documents network-first, so the reload gets
 * the new build rather than the cached old one. A second failure within
 * `CHUNK_RELOAD_WINDOW_MS` is not a stale tab, it is a connection or a broken build, and
 * reloading again would only loop; it renders the error screen instead, with a retry the
 * person controls.
 */
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';
import { Button, EmptyState } from '@oprappo/ui';
import { FRAME_BOX } from './responsive/frame.js';

/** AUTHORED — the unknown-path screen. See the file header; listed in docs/copy-review/W1-03.md. */
export const NOT_FOUND_COPY = {
  title_bn: 'এই ঠিকানায় কোনো পাতা নেই',
  body_bn: 'লিংকটি পুরোনো বা ভুল হতে পারে। হোম থেকে আবার খুঁজে নিন।',
  /** DRAWN — `Oprappo P0 Screens.dc.html`. */
  home_bn: 'হোমে যান',
} as const;

/** AUTHORED — the screen a route that threw falls back to. Listed in docs/copy-review/W1-03.md. */
export const ROUTE_ERROR_COPY = {
  title_bn: 'পাতাটি খোলা গেল না',
  body_bn: 'আবার চেষ্টা করে দেখুন। তাতেও না খুললে হোম থেকে শুরু করুন।',
  /** DRAWN — `Oprappo P0 States.dc.html`. */
  retry_bn: 'আবার চেষ্টা করুন',
  /** DRAWN — `Oprappo P0 Screens.dc.html`. */
  home_bn: 'হোমে যান',
} as const;

/**
 * Is this the failure of a lazily loaded chunk, as opposed to a bug in a screen?
 *
 * Matched on the message each engine actually throws, because there is no error class for
 * it: Chromium's `TypeError: Failed to fetch dynamically imported module: …`, Firefox's
 * `error loading dynamically imported module`, WebKit's `Importing a module script
 * failed.`, and Vite's own preload helper, which throws `Unable to preload CSS for …` when
 * a chunk's stylesheet is the part that went missing. A bug in a screen matches none of
 * them, and must not be answered with a reload that hides it.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|loading chunk [\w-]+ failed/i.test(
    `${error.name} ${error.message}`,
  );
}

/** sessionStorage key holding the time of the last chunk-failure reload, in ms. */
export const CHUNK_RELOAD_KEY = 'oprappo:chunk-reload-at';

/**
 * How long after one reload a second chunk failure is taken to mean "reloading did not
 * help". Long enough for a slow reload on 2G to land and fail again inside it; short
 * enough that a tab left open across a SECOND deploy hours later still recovers.
 */
export const CHUNK_RELOAD_WINDOW_MS = 30_000;

type ReadableStorage = Pick<Storage, 'getItem'>;
type WritableStorage = Pick<Storage, 'setItem'>;

/**
 * May the error element reload now? A pure read, so a render can ask it.
 *
 * No storage — a private window, blocked site data, an accessor that throws — means NO.
 * Without somewhere to write "already tried", a reload cannot know it is the second one,
 * and an unguarded reload on a chunk that will never load is an infinite loop on the
 * customer's data plan. The error screen and its retry button are the safe answer.
 */
export function mayReloadForChunk(storage: ReadableStorage | null, now: number): boolean {
  if (storage === null) return false;
  try {
    const last = Number(storage.getItem(CHUNK_RELOAD_KEY) ?? NaN);
    return !Number.isFinite(last) || now - last > CHUNK_RELOAD_WINDOW_MS || now < last;
  } catch {
    return false;
  }
}

/** Record the reload about to happen. Returns false when it could not be recorded. */
export function markChunkReload(storage: WritableStorage | null, now: number): boolean {
  if (storage === null) return false;
  try {
    storage.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    return false;
  }
}

/** `window.sessionStorage`, or null wherever reading it throws or there is no window. */
function sessionStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

/** The shared frame: sand ground, the phone column, the empty state centred in it. */
function FallbackFrame({ kind, children }: { kind: string; children: JSX.Element }): JSX.Element {
  return (
    <main
      data-fallback={kind}
      data-copy-status="authored"
      className={FRAME_BOX + ' bg-sand justify-center px-[16px]'}
    >
      {children}
    </main>
  );
}

/** The view, with its one way out passed in, so a test can render it without a router. */
export function NotFoundView({ onHome }: { onHome?: () => void }): JSX.Element {
  return (
    <FallbackFrame kind="not-found">
      <EmptyState
        titleBn={NOT_FOUND_COPY.title_bn}
        bodyBn={NOT_FOUND_COPY.body_bn}
        actionLabelBn={NOT_FOUND_COPY.home_bn}
        onAction={onHome}
      />
    </FallbackFrame>
  );
}

/** The `*` route: every path no group mounts on this host. */
export function NotFound(): JSX.Element {
  const navigate = useNavigate();
  return <NotFoundView onHome={() => navigate('/')} />;
}

/**
 * The route-error view. Both ways out are full page loads rather than router navigations:
 * whatever threw is still in memory, and a navigation inside the same app instance can
 * land on the same broken state.
 */
export function RouteErrorView({
  onRetry,
  onHome,
}: {
  onRetry?: () => void;
  onHome?: () => void;
}): JSX.Element {
  return (
    <FallbackFrame kind="route-error">
      <div>
        <EmptyState
          titleBn={ROUTE_ERROR_COPY.title_bn}
          bodyBn={ROUTE_ERROR_COPY.body_bn}
          actionLabelBn={ROUTE_ERROR_COPY.retry_bn}
          onAction={onRetry}
        />
        <Button tone="tertiary" size="md" className="mt-[9px]" onClick={onHome}>
          {ROUTE_ERROR_COPY.home_bn}
        </Button>
      </div>
    </FallbackFrame>
  );
}

/**
 * The root `errorElement`. Mounted once, around every route, in `App.tsx`.
 *
 * A stale chunk reloads once (see the file header). A 404 the router raises itself —
 * which the `*` route should make unreachable — gets the not-found screen rather than
 * the crash screen, because that is what happened.
 */
export function RouteError(): JSX.Element {
  const error = useRouteError();
  // Set when the "already tried" mark could not be written: storage that reads but will
  // not write (a full quota) must not leave a blank ground waiting for a reload that the
  // guard has no way to make safe.
  const [unmarkable, setUnmarkable] = useState(false);
  const reload =
    !unmarkable && isChunkLoadError(error) && mayReloadForChunk(sessionStore(), Date.now());

  useEffect(() => {
    if (!reload) return;
    if (markChunkReload(sessionStore(), Date.now())) window.location.reload();
    else setUnmarkable(true);
  }, [reload]);

  if (reload) {
    // A blank ground for the instant before the reload lands. No text: there is nothing
    // to read, and a sentence that flashes for a quarter-second is worse than none.
    return <main aria-busy="true" className={FRAME_BOX + ' bg-sand'} />;
  }
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFoundView onHome={() => window.location.assign('/')} />;
  }
  return (
    <RouteErrorView
      onRetry={() => window.location.reload()}
      onHome={() => window.location.assign('/')}
    />
  );
}
